import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';

import { IVolumeOption } from 'interfaces/businessTool';

import { ObjectValues } from 'helpers/types';

export const BUSINESS_TOOL_SECTIONS = {
  GLOBAL: 'GLOBAL',
  OVERVIEW: 'OVERVIEW',
  EXECUTIVE_SUMMARY: 'EXECUTIVE_SUMMARY',
  ABOUT_COMPANY: 'ABOUT_COMPANY',
  PRODUCTS_AND_SERVICES: 'PRODUCTS_AND_SERVICES',
  MARKET_ANALYSIS: 'MARKET_ANALYSIS',
  MARKETING_AND_SALES: 'MARKETING_AND_SALES',
  BUSINESS_OPERATIONS: 'BUSINESS_OPERATIONS',
  MANAGEMENT_AND_ORGANIZATION: 'MANAGEMENT_AND_ORGANIZATION',
  IMPLEMENTATION_PLAN: 'IMPLEMENTATION_PLAN',
  RAISING_AND_ALLOCATING_FUNDS: 'RAISING_AND_ALLOCATING_FUNDS',
  LEGAL_FRAMEWORKS_AND_RISKS: 'LEGAL_FRAMEWORKS_AND_RISKS',
  FINANCIAL_PLAN: 'FINANCIAL_PLAN',
} as const;

export type IBusinessToolSections = ObjectValues<typeof BUSINESS_TOOL_SECTIONS>;

export const BUSINESS_TOOL_SUB_SECTIONS = {
  OVERVIEW: {
    COMPANY: 'company',
    PROJECT: 'project',
    MARKET_CUSTOMERS: 'marketAndCustomers',
    FINANCIAL_INFO: 'financialInfo',
    FUNDING_NEEDS: 'fundingNeeds',
  },
  EXECUTIVE_SUMMARY: {
    GOALS_AND_OBJECTIVES: 'goalsAndObjectives',
    COMPANY_DESCRIPTION: 'companyDescription',
    PRODUCTS_OR_SERVICES: 'productsOrServices',
    MARKET_AND_CUSTOMERS: 'marketAndCustomers',
    ORGANIZATION_AND_TEAM: 'organizationAndTeam',
    FINANCIAL_INFORMATION: 'financialInformation',
    FINANCING_NEEDS: 'financingNeeds',
    RISKS: 'risks',
  },
  ABOUT_COMPANY: {
    GENERAL_INFO: 'generalInfo',
    COMPANY_HISTORY: 'companyHistory',
    MISSION_AND_STRATEGY: 'missionAndStrategy',
    SWOT: 'swot',
  } as const,
  PRODUCTS_AND_SERVICES: {
    DESCRIPTION: 'description',
    ADVANTAGES: 'advantages',
    R_AND_D: 'randd',
    DELIVERY_AND_SUPPORT: 'deliveryAndSupport',
  } as const,
  MARKET_ANALYSIS: {
    MARKET_OVERVIEW: 'marketOverview',
    COMPETITIVE_ANALYSIS: 'competitiveAnalysis',
    CONSUMER_NEEDS: 'consumerNeeds',
  },
  MARKETING_AND_SALES: {
    SALES_PLAN: 'salesPlan',
    MARKETING_PLAN: 'marketingPlan',
  },
  BUSINESS_OPERATIONS: {
    LOCATION_AND_PREMISES: 'locationAndPremises',
    EQUIPMENT: 'equipment',
    ASSETS: 'otherAssets',
    TECHNOLOGICAL_PROCESS: 'technologicalProcess',
    WORKING_RESOURCES: 'workingResources',
    PARTNERS: 'partnersSection',
    PRODUCTION: 'production',
    QUALITY: 'quality',
  },
  MANAGEMENT_AND_ORGANIZATION: {
    MANAGEMENT_TEAM: 'managementTeam',
    EXTERNAL_PARTNERS_AND_ADVISERS: 'externalPartnersAndAdvisers',
    STAFF_ORGANIZATION: 'staffOrganization',
  },
  FINANCIAL_PLAN: {
    KEY_ASSUMPTIONS: 'keyAssumptions',
    REVENUE_PLAN: 'revenuePlan',
    PLANNED_EXPENSES: 'plannedExpenses',
    PROFIT_AND_LOSS_PROJECTION: 'profitAndLossProjection',
    CASH_FLOW_ELEMENTS: 'cashFlowElements',
    FINANCIAL_PERFORMANCE: 'financialPerformance',
    OPENING_BALANCE_SHEETS: 'openingBalanceSheets',
    PROJECTED_BALANCE_SHEETS: 'projectedBalanceSheets',
    PROJECTED_CASH_FLOW: 'projectedCashFlow',
  },
} as const;

export const BUSINESS_TOOL_MODE = {
  EDIT: 'edit',
  PREVIEW: 'preview',
} as const;

export type IBusinessToolModes = ObjectValues<typeof BUSINESS_TOOL_MODE>;

export const VOLUME_IN_OPTION_VALUES = {
  PIECES: { label: 'Pieces (Pcs)', value: 'pieces' },
  DOLLAR: { label: 'Dollar', value: 'dollar' },
  PERCENTAGE: { label: 'Percentage', value: 'percentage' },
  ORDERS: { label: 'Orders (ord)', value: 'orders' },
  PACKS: { label: 'Packs (pk)', value: 'packs' },
  LITER: { label: 'Liter (L)', value: 'liter' },
  KILOGRAM: { label: 'Kilogram (Kg)', value: 'kilograms' },
  TONNE: { label: 'Tonne (t)', value: 'tonnes' },
  POUND: { label: 'Pound (lb)', value: 'pounds' },
  CUBIC_METER: { label: 'Cubic meter (m\u00B3)', value: 'cubic_meter' },
  GALLON: { label: 'Gallon (gal)', value: 'gallon' },
  ACRE: { label: 'Acre (ac)', value: 'acre' },
  KILOWATT_HOUR: { label: 'Kilowatt-hour (kWh)', value: 'kilowatt_hour' },
  SQUARE_METER: { label: 'Square meter (m\u00B2)', value: 'square_meter' },
  GIGABYTE: { label: 'Gigabyte (GB)', value: 'gigabyte' },
  HOURS: { label: 'Hours (H)', value: 'hours' },
  BARREL: { label: 'Barrel (bbl)', value: 'barrel' },
  UNITS: { label: 'Units (units)', value: 'units' },
  FEET: { label: 'Feet (ft)', value: 'feet' },
  SQUARE_FEET: { label: 'Square feet (ft\u00B2)', value: 'square_feet' },
  CUBIC_FEET: { label: 'Cubic feet (ft\u00B3)', value: 'cubic_feet' },
  OUNCES: { label: 'Ounces (oz)', value: 'ounces' },
  MILES: { label: 'Miles (M)', value: 'miles' },
} as const;

export const useGetVolumeInOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const VOLUME_IN_OPTION_VALUES = {
      PIECES: { label: t('unitsOfMeasurement.pieces'), value: 'pieces' },
      DOLLAR: { label: t('unitsOfMeasurement.dollar'), value: 'dollar' },
      PERCENTAGE: { label: t('unitsOfMeasurement.percentage'), value: 'percentage' },
      ORDERS: { label: t('unitsOfMeasurement.orders'), value: 'orders' },
      PACKS: { label: t('unitsOfMeasurement.packs'), value: 'packs' },
      LITER: { label: t('unitsOfMeasurement.liter'), value: 'liter' },
      KILOGRAM: { label: t('unitsOfMeasurement.kilograms'), value: 'kilograms' },
      TONNE: { label: t('unitsOfMeasurement.tonnes'), value: 'tonnes' },
      POUND: { label: t('unitsOfMeasurement.pounds'), value: 'pounds' },
      CUBIC_METER: { label: t('unitsOfMeasurement.cubicMeter'), value: 'cubic_meter' },
      GALLON: { label: t('unitsOfMeasurement.gallon'), value: 'gallon' },
      ACRE: { label: t('unitsOfMeasurement.acre'), value: 'acre' },
      KILOWATT_HOUR: { label: t('unitsOfMeasurement.kilowattHour'), value: 'kilowatt_hour' },
      SQUARE_METER: { label: t('unitsOfMeasurement.squareMeter'), value: 'square_meter' },
      GIGABYTE: { label: t('unitsOfMeasurement.gigabyte'), value: 'gigabyte' },
      HOURS: { label: t('unitsOfMeasurement.hours'), value: 'hours' },
      BARREL: { label: t('unitsOfMeasurement.barrel'), value: 'barrel' },
      UNITS: { label: t('unitsOfMeasurement.units'), value: 'units' },
      FEET: { label: t('unitsOfMeasurement.feet'), value: 'feet' },
      SQUARE_FEET: { label: t('unitsOfMeasurement.squareFeet'), value: 'square_feet' },
      CUBIC_FEET: { label: t('unitsOfMeasurement.cubicFeet'), value: 'cubic_feet' },
      OUNCES: { label: t('unitsOfMeasurement.ounces'), value: 'ounces' },
      MILES: { label: t('unitsOfMeasurement.miles'), value: 'miles' },
    };

    const VOLUME_IN_OPTIONS: IVolumeOption[] = [
  {
    label: VOLUME_IN_OPTION_VALUES.PIECES.label,
    value: VOLUME_IN_OPTION_VALUES.PIECES.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.ORDERS.label,
    value: VOLUME_IN_OPTION_VALUES.ORDERS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.PACKS.label,
    value: VOLUME_IN_OPTION_VALUES.PACKS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.LITER.label,
    value: VOLUME_IN_OPTION_VALUES.LITER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.KILOGRAM.label,
    value: VOLUME_IN_OPTION_VALUES.KILOGRAM.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.TONNE.label,
    value: VOLUME_IN_OPTION_VALUES.TONNE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.POUND.label,
    value: VOLUME_IN_OPTION_VALUES.POUND.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.CUBIC_METER.label,
    value: VOLUME_IN_OPTION_VALUES.CUBIC_METER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.GALLON.label,
    value: VOLUME_IN_OPTION_VALUES.GALLON.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.ACRE.label,
    value: VOLUME_IN_OPTION_VALUES.ACRE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.KILOWATT_HOUR.label,
    value: VOLUME_IN_OPTION_VALUES.KILOWATT_HOUR.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.SQUARE_METER.label,
    value: VOLUME_IN_OPTION_VALUES.SQUARE_METER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.GIGABYTE.label,
    value: VOLUME_IN_OPTION_VALUES.GIGABYTE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.HOURS.label,
    value: VOLUME_IN_OPTION_VALUES.HOURS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.BARREL.label,
    value: VOLUME_IN_OPTION_VALUES.BARREL.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.UNITS.label,
    value: VOLUME_IN_OPTION_VALUES.UNITS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.FEET.label,
    value: VOLUME_IN_OPTION_VALUES.FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.SQUARE_FEET.label,
    value: VOLUME_IN_OPTION_VALUES.SQUARE_FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.CUBIC_FEET.label,
    value: VOLUME_IN_OPTION_VALUES.CUBIC_FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.OUNCES.label,
    value: VOLUME_IN_OPTION_VALUES.OUNCES.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.MILES.label,
    value: VOLUME_IN_OPTION_VALUES.MILES.value,
  },
];
    return VOLUME_IN_OPTIONS;
  }, [t, i18n]);
};

export const VOLUME_IN_OPTIONS: IVolumeOption[] = [
  {
    label: VOLUME_IN_OPTION_VALUES.PIECES.label,
    value: VOLUME_IN_OPTION_VALUES.PIECES.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.ORDERS.label,
    value: VOLUME_IN_OPTION_VALUES.ORDERS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.PACKS.label,
    value: VOLUME_IN_OPTION_VALUES.PACKS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.LITER.label,
    value: VOLUME_IN_OPTION_VALUES.LITER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.KILOGRAM.label,
    value: VOLUME_IN_OPTION_VALUES.KILOGRAM.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.TONNE.label,
    value: VOLUME_IN_OPTION_VALUES.TONNE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.POUND.label,
    value: VOLUME_IN_OPTION_VALUES.POUND.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.CUBIC_METER.label,
    value: VOLUME_IN_OPTION_VALUES.CUBIC_METER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.GALLON.label,
    value: VOLUME_IN_OPTION_VALUES.GALLON.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.ACRE.label,
    value: VOLUME_IN_OPTION_VALUES.ACRE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.KILOWATT_HOUR.label,
    value: VOLUME_IN_OPTION_VALUES.KILOWATT_HOUR.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.SQUARE_METER.label,
    value: VOLUME_IN_OPTION_VALUES.SQUARE_METER.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.GIGABYTE.label,
    value: VOLUME_IN_OPTION_VALUES.GIGABYTE.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.HOURS.label,
    value: VOLUME_IN_OPTION_VALUES.HOURS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.BARREL.label,
    value: VOLUME_IN_OPTION_VALUES.BARREL.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.UNITS.label,
    value: VOLUME_IN_OPTION_VALUES.UNITS.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.FEET.label,
    value: VOLUME_IN_OPTION_VALUES.FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.SQUARE_FEET.label,
    value: VOLUME_IN_OPTION_VALUES.SQUARE_FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.CUBIC_FEET.label,
    value: VOLUME_IN_OPTION_VALUES.CUBIC_FEET.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.OUNCES.label,
    value: VOLUME_IN_OPTION_VALUES.OUNCES.value,
  },
  {
    label: VOLUME_IN_OPTION_VALUES.MILES.label,
    value: VOLUME_IN_OPTION_VALUES.MILES.value,
  },
];

// ---- DIMENSIONALITY ----

export const DIMENSIONALITY_VALUES = {
  SINGLE: 'single',
  THOUSAND: 'thousand',
  MILLION: 'million',
  BILLION: 'billion',
} as const;

export type IDimensionalityOption = ObjectValues<typeof DIMENSIONALITY_VALUES>;

export const DIMENSIONALITY_OPTIONS = [
  { label: t('dimensionality.' + DIMENSIONALITY_VALUES.SINGLE), value: DIMENSIONALITY_VALUES.SINGLE },
  { label: t('dimensionality.' + DIMENSIONALITY_VALUES.THOUSAND), value: DIMENSIONALITY_VALUES.THOUSAND },
  { label: t('dimensionality.' + DIMENSIONALITY_VALUES.MILLION), value: DIMENSIONALITY_VALUES.MILLION },
  { label: t('dimensionality.' + DIMENSIONALITY_VALUES.BILLION), value: DIMENSIONALITY_VALUES.BILLION },
];

export const useGetDimensionalityOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const DIMENSIONALITY_OPTIONS = [
      { label: t('dimensionality.' + DIMENSIONALITY_VALUES.SINGLE), value: DIMENSIONALITY_VALUES.SINGLE },
      { label: t('dimensionality.' + DIMENSIONALITY_VALUES.THOUSAND), value: DIMENSIONALITY_VALUES.THOUSAND },
      { label: t('dimensionality.' + DIMENSIONALITY_VALUES.MILLION), value: DIMENSIONALITY_VALUES.MILLION },
      { label: t('dimensionality.' + DIMENSIONALITY_VALUES.BILLION), value: DIMENSIONALITY_VALUES.BILLION },
    ];
    return DIMENSIONALITY_OPTIONS;
  }, [t, i18n]);
};
